import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { BusinessDocumentService } from '@/services/business-document.service';
import {
  BusinessDocument,
  BusinessDocumentType,
  BusinessDocumentStatus,
  BusinessDocumentTypeLabels,
  BusinessDocumentStatusLabels,
} from '@/models/business-document.model';
import { format } from 'date-fns';
import { Button, Card, LoadingSpinner, EmptyState, SectionHeader, FormInput, FormSelect, FormTextarea, Badge } from '@/components/common';
import { FileText, ArrowLeft, Save, Building2, Calendar, CheckCircle2, Download, X } from 'lucide-react';

export const BusinessDocumentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState<Partial<BusinessDocument>>({
    name: '',
    type: BusinessDocumentType.other,
    status: BusinessDocumentStatus.active,
    tags: [],
  });
  const [file, setFile] = useState<File | null>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');

  const { data: document, isLoading } = useQuery({
    queryKey: ['businessDocument', id],
    queryFn: () => (id ? BusinessDocumentService.getDocumentById(id) : null),
    enabled: !!id && isEditing,
  });

  useEffect(() => {
    if (document) {
      setFormData({
        name: document.name,
        type: document.type,
        documentNumber: document.documentNumber,
        status: document.status,
        issueDate: document.issueDate,
        expirationDate: document.expirationDate,
        renewalDate: document.renewalDate,
        description: document.description,
        issuingAuthority: document.issuingAuthority,
        notes: document.notes,
        tags: document.tags || [],
      });
      setExistingFileUrl(document.fileUrl || null);
    }
  }, [document]);

  const mutation = useMutation({
    mutationFn: async (data: Partial<BusinessDocument>) => {
      if (isEditing && id) {
        await BusinessDocumentService.updateDocument(id, data, file || undefined);
      } else {
        await BusinessDocumentService.createDocument(
          {
            ...data,
            createdBy: profile?.id || '',
            createdByName: profile?.fullName || profile?.email || '',
          },
          file || undefined
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businessDocuments'] });
      navigate('/business-documents');
    },
    onError: (error: any) => {
      console.error('Error saving document:', error);
      alert(error?.message || 'Failed to save document');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      alert('Document name is required');
      return;
    }

    if (!isEditing && !file && !existingFileUrl) {
      alert('Please upload a document file');
      return;
    }

    mutation.mutate(formData);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter(t => t !== tag) || [],
    });
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading document..." fullScreen />;
  }

  if (isEditing && !document) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={FileText}
          title="Document not found"
          description="The requested document does not exist."
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/business-documents')}>
              Back to Documents
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        title={isEditing ? 'Edit Business Document' : 'Add Business Document'}
        description={isEditing ? 'Update document information' : 'Add a new business registration document'}
      />

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <FormInput
                label="Document Name"
                icon={FileText}
                required
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Federal EIN Certificate"
              />
            </div>

            <FormSelect
              label="Document Type"
              icon={FileText}
              required
              options={Object.entries(BusinessDocumentTypeLabels).map(([value, label]) => ({
                value,
                label,
              }))}
              value={formData.type || BusinessDocumentType.other}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as BusinessDocumentType })}
            />

            <FormSelect
              label="Status"
              icon={CheckCircle2}
              required
              options={Object.entries(BusinessDocumentStatusLabels).map(([value, label]) => ({
                value,
                label,
              }))}
              value={formData.status || BusinessDocumentStatus.active}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as BusinessDocumentStatus })}
            />

            <FormInput
              label="Document Number"
              icon={FileText}
              value={formData.documentNumber || ''}
              onChange={(e) => setFormData({ ...formData, documentNumber: e.target.value })}
              placeholder="EIN, DUNS, License #, etc."
            />

            <FormInput
              label="Issuing Authority"
              icon={Building2}
              value={formData.issuingAuthority || ''}
              onChange={(e) => setFormData({ ...formData, issuingAuthority: e.target.value })}
              placeholder="e.g., IRS, State of California"
            />

            <FormInput
              label="Issue Date"
              type="date"
              icon={Calendar}
              value={formData.issueDate ? format(formData.issueDate, 'yyyy-MM-dd') : ''}
              onChange={(e) => setFormData({
                ...formData,
                issueDate: e.target.value ? new Date(e.target.value) : undefined,
              })}
            />

            <FormInput
              label="Expiration Date"
              type="date"
              icon={Calendar}
              value={formData.expirationDate ? format(formData.expirationDate, 'yyyy-MM-dd') : ''}
              onChange={(e) => setFormData({
                ...formData,
                expirationDate: e.target.value ? new Date(e.target.value) : undefined,
              })}
            />

            <FormInput
              label="Renewal Date"
              type="date"
              icon={Calendar}
              value={formData.renewalDate ? format(formData.renewalDate, 'yyyy-MM-dd') : ''}
              onChange={(e) => setFormData({
                ...formData,
                renewalDate: e.target.value ? new Date(e.target.value) : undefined,
              })}
            />
          </div>

          <FormTextarea
            label="Description"
            icon={FileText}
            rows={3}
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Additional information about this document..."
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-2" />
              Document File {isEditing && existingFileUrl ? '(Optional - upload new to replace)' : '(Required)'}
            </label>
            {existingFileUrl && !file && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Current File</p>
                      <a
                        href={existingFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" />
                        Download current file
                      </a>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={X}
                    onClick={() => setExistingFileUrl(null)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            )}
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              required={!isEditing || (!existingFileUrl && !file)}
            />
            {file && (
              <p className="mt-2 text-sm text-gray-600">
                Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Add a tag and press Enter"
                className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <Button type="button" variant="outline" onClick={addTag}>Add</Button>
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <Badge key={tag} variant="info" size="sm">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 hover:text-red-600"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <FormTextarea
            label="Internal Notes"
            icon={FileText}
            rows={3}
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Internal notes about this document..."
          />

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              icon={ArrowLeft}
              onClick={() => navigate('/business-documents')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              isLoading={mutation.isPending}
              iconPosition="right"
            >
              {isEditing ? 'Update Document' : 'Create Document'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
